
import { supabase } from '@/integrations/supabase/client';
import type { ResearchLibraryItem, ResearchLibraryResponse, UseResearchLibraryOptions } from './types';
import { ResearchQueryBuilder, validateQueryOptions } from './research-query-builder';

// Helper function to safely convert Json to Record<string, any>
const safeJsonToRecord = (json: any): Record<string, any> => {
  if (json === null || json === undefined) {
    return {};
  }
  if (typeof json === 'object' && !Array.isArray(json)) {
    return json as Record<string, any>;
  }
  return {};
};

// Helper function to safely convert Json to array
const safeJsonToArray = (json: any): any[] => {
  if (Array.isArray(json)) {
    return json;
  }
  return [];
};

// Transform database result to ResearchLibraryItem
const transformResearchData = (items: any[]): ResearchLibraryItem[] => {
  return items.map(item => ({
    id: item.id,
    user_id: item.user_id,
    title: item.title,
    content: item.content,
    research_type: item.research_type,
    original_prompt: item.original_prompt,
    metadata: safeJsonToRecord(item.metadata),
    sources: safeJsonToArray(item.sources),
    related_questions: Array.isArray(item.related_questions) ? item.related_questions : [],
    usage: safeJsonToRecord(item.usage),
    research_parameters: safeJsonToRecord(item.research_parameters),
    favorite_count: item.favorite_count,
    generated_research_count: item.generated_research_count || 0,
    is_favorited: item.research_favorites && item.research_favorites.length > 0,
    created_at: item.created_at,
    updated_at: item.updated_at,
  }));
};

// Helper function to calculate date threshold based on timeframe (same as ResearchQueryBuilder)
const calculateDateThreshold = (timeframe: string): string => {
  const today = new Date();
  let thresholdDate = new Date(today);
  
  switch (timeframe) {
    case 'last_7_days':
    case '7d':
      thresholdDate.setDate(today.getDate() - 7);
      break;
    case 'last_30_days':
    case '30d':
      thresholdDate.setDate(today.getDate() - 30);
      break;
    case 'last_3_months':
    case '3m':
      thresholdDate.setMonth(today.getMonth() - 3);
      break;
    case 'last_6_months':
    case '6m':
      thresholdDate.setMonth(today.getMonth() - 6);
      break;
    case 'last_year':
    case '1y':
      thresholdDate.setFullYear(today.getFullYear() - 1);
      break;
    default:
      return '';
  }
  
  // Return ISO string for created_at timestamp comparison
  return thresholdDate.toISOString();
};

// Helper function to apply keyword-based search to any query
const applyKeywordSearch = (query: any, searchQuery: string) => {
  console.log(`🔍 Fallback: Adding keyword-based text search filter: "${searchQuery}"`);
  
  // Split search query into individual keywords and trim whitespace
  const keywords = searchQuery.trim().split(/\s+/).filter(keyword => keyword.length > 0);
  
  if (keywords.length === 0) {
    return query;
  }
  
  console.log(`🔍 Fallback: Processing ${keywords.length} keyword(s):`, keywords);
  
  // Apply each keyword as a separate filter (creates AND logic between keywords)
  keywords.forEach((keyword) => {
    // Each keyword must appear in at least one of the three fields
    query = query.or(`title.ilike.%${keyword}%,content.ilike.%${keyword}%,original_prompt.ilike.%${keyword}%`);
  });
  
  return query;
};

// Create a simple fallback query that always works - now with corrected research_type handling
// SCALABLE: Research sharing already optimized via scope parameter ('all' vs 'mine')
const createFallbackQuery = async (userId: string, options: UseResearchLibraryOptions, pageParam: number, pageSize: number) => {
  console.log('Using simple fallback query with corrected research_type handling');
  
  let query = supabase
    .from('research_library')
    .select(`
      *,
      research_favorites!left(id)
    `);

  // Apply scope filter - only filter by user_id if scope is 'mine'
  // SCALABLE: 'all' scope shows global research pool, 'mine' shows user-specific
  // This design already supports collaboration without additional queries
  if (options.scope === 'mine') {
    query = query.eq('user_id', userId);
  }

  // Apply keyword-based search
  if (options.searchQuery) {
    query = applyKeywordSearch(query, options.searchQuery);
  }

  // Fixed research_type filter for jsonb strings
  if (options.researchType) {
    console.log(`Fallback: Adding research_type filter for: ${options.researchType}`);
    try {
      // For jsonb containing strings, we compare with the quoted string
      query = query.eq('research_type', `"${options.researchType}"`);
    } catch (error) {
      console.warn('Fallback research_type filter failed, skipping:', error);
      // Skip research_type filter in fallback if it fails
    }
  }

  // Add timeframe filter using created_at column (same logic as enhanced query)
  if (options.timeframe && options.timeframe !== 'current') {
    const thresholdDate = calculateDateThreshold(options.timeframe);
    if (thresholdDate) {
      console.log(`Fallback query: Adding created_at filter: created_at >= ${thresholdDate}`);
      query = query.gte('created_at', thresholdDate);
    }
  }

  // Handle favorites with separate query - favorites are always user-specific
  if (options.showFavorites) {
    query = supabase
      .from('research_library')
      .select(`
        *,
        research_favorites!inner(id)
      `)
      .eq('user_id', userId)
      .eq('research_favorites.user_id', userId);

    // Re-apply keyword-based search
    if (options.searchQuery) {
      query = applyKeywordSearch(query, options.searchQuery);
    }
    
    // Re-apply research_type filter for favorites query with corrected handling
    if (options.researchType) {
      try {
        query = query.eq('research_type', `"${options.researchType}"`);
      } catch (error) {
        console.warn('Fallback favorites research_type filter failed, skipping:', error);
      }
    }
    
    // Re-apply timeframe filter for favorites query
    if (options.timeframe && options.timeframe !== 'current') {
      const thresholdDate = calculateDateThreshold(options.timeframe);
      if (thresholdDate) {
        console.log(`Fallback favorites query: Adding created_at filter: created_at >= ${thresholdDate}`);
        query = query.gte('created_at', thresholdDate);
      }
    }
  }

  return query
    .order('favorite_count', { ascending: false })        // Most favorites first
    .order('generated_research_count', { ascending: false }) // Then most generative research
    .order('created_at', { ascending: false })             // Then newest first
    .range(pageParam * pageSize, (pageParam * pageSize) + pageSize - 1);
};

export class ResearchQueryService {
  static async fetchResearchLibrary(
    options: UseResearchLibraryOptions = {},
    pageParam: number = 0
  ): Promise<ResearchLibraryResponse> {
    const {
      searchQuery,
      researchType,
      showFavorites = false,
      pageSize = 12,
      scope = 'mine',
    } = options;

    console.log('Fetching research library with params:', { 
      pageParam, 
      pageSize, 
      searchQuery, 
      researchType, 
      showFavorites,
      scope,
      timeframe: options.timeframe,
      country: options.country, // Changed from region to country
      sourceQuality: options.sourceQuality,
      typeSpecificFilters: options.typeSpecificFilters
    });
    
    try {
      // Validate authentication
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Validate query parameters
      if (!validateQueryOptions(options)) {
        console.warn('Invalid query options, using fallback');
        const fallbackResult = await createFallbackQuery(user.id, options, pageParam, pageSize);
        const { data: fallbackItems, error: fallbackError, count } = await fallbackResult;
        
        if (fallbackError) throw fallbackError;
        
        return {
          data: transformResearchData(fallbackItems || []),
          nextPage: fallbackItems && fallbackItems.length === pageSize ? pageParam + 1 : undefined,
          count
        };
      }

      // Try the enhanced query builder with proper JSONB filtering
      try {
        console.log('Using enhanced query builder with corrected JSONB filtering');
        const queryBuilder = new ResearchQueryBuilder(user.id, scope);
        
        // Apply favorites filter first (changes base query) - favorites are always user-specific
        queryBuilder.applyFavoritesFilter(showFavorites, user.id);
        
        // Apply other filters - pass the full options object so research type is available
        queryBuilder
          .applyTextSearch(searchQuery)
          .applyResearchType(researchType)
          .applyParameterFilters(options)
          .applySortingAndPagination(pageParam, pageSize);

        const { data: researchItems, error, count } = await queryBuilder.execute();

        if (error) {
          console.error('Enhanced query failed with error:', error);
          throw error;
        }

        console.log('Enhanced query successful:', { count, itemsLength: researchItems?.length });

        return {
          data: transformResearchData(researchItems || []),
          nextPage: researchItems && researchItems.length === pageSize ? pageParam + 1 : undefined,
          count
        };

      } catch (queryError: any) {
        console.warn('Enhanced query failed, using fallback:', queryError);
        
        // Use simple fallback query
        const fallbackResult = await createFallbackQuery(user.id, options, pageParam, pageSize);
        const { data: fallbackItems, error: fallbackError, count } = await fallbackResult;
        
        if (fallbackError) {
          throw fallbackError;
        }

        console.log('Fallback query successful:', { count, itemsLength: fallbackItems?.length });
        
        return {
          data: transformResearchData(fallbackItems || []),
          nextPage: fallbackItems && fallbackItems.length === pageSize ? pageParam + 1 : undefined,
          count
        };
      }

    } catch (error) {
      console.error('Fatal error in fetchResearchLibrary:', error);
      
      // Return empty result instead of throwing to prevent error loops
      return {
        data: [],
        nextPage: undefined,
        count: 0
      };
    }
  }
}
